import React from 'react';
import './agentDisplay.css';

/**
 * Hybrid agents (2026-11-02): someone who also works the other team's tickets until the two teams are one. The other team is on the
 * login (`second_team`, or `secondTeam` on a live PHM row). The (H) is drawn from it here and never typed into a name: the GrafiX
 * sheet, PHM, the Schedule and the Dossier import find people by exact name, so a "(H)" in one would lose the person.
 */
export const agentSecondTeam = (user) => String(user?.second_team || user?.secondTeam || '').trim();

/** The (H) after a name, with the other team on hover. Nothing for anyone who is not a hybrid. */
export const HybridMark = ({ agent }) => {
  const second = agentSecondTeam(agent);
  if (!second) return null;
  return <span className="agent-hybrid-mark" title={`Hybrid: also works for ${second}`}>(H)</span>;
};

/** PHM / Zendesk nickname (e.g. Randy) - matched to roster by email, else stored users.real_name. */
export const agentNickname = (user) => {
  const fromPhm = String(user?.phm_nickname || user?.zendeskDisplayName || user?.nickname || '').trim();
  if (fromPhm) return fromPhm;
  const stored = String(user?.real_name || '').trim();
  const real = String(user?.username || user?.rosterUsername || user?.agentName || '').trim();
  if (stored && stored.toLowerCase() !== real.toLowerCase()) return stored;
  return '';
};

/** Roster real name (users.username, e.g. Angel Kezhov). */
export const agentRealName = (user) =>
  String(user?.username || user?.rosterUsername || user?.agentName || '').trim();

/** Primary label for tables - real name when set, else nickname. */
export const agentPrimaryName = (user) => agentRealName(user) || agentNickname(user) || '-';

/** Unified picker / admin label: "Real Name - Nickname". */
export const agentDisplayLabel = (user) => {
  const real = agentRealName(user);
  const nick = agentNickname(user);
  const mark = agentSecondTeam(user) ? ' (H)' : '';
  if (real && nick && real.toLowerCase() !== nick.toLowerCase()) return `${real} - ${nick}${mark}`;
  return `${real || nick || 'Unknown'}${mark}`;
};

/** PHM merged/live row: roster username = real name, Zendesk profile name = nickname. */
export const phmRowDisplayLabel = (row) => {
  const real = String(row?.rosterUsername || row?.username || '').trim();
  const nick = String(row?.zendeskDisplayName || '').trim();
  if (real && nick && real.toLowerCase() !== nick.toLowerCase()) return `${real} - ${nick}`;
  return real || nick || 'Unknown';
};

/**
 * A name followed by the (H). In a narrow column the name gives way with an ellipsis and the mark always stays, so a long name never hides that
 * someone is a hybrid. Anyone else gets the bare text, as before.
 */
const nameWithMark = (text, agent) => (agentSecondTeam(agent)
  ? <span className="agent-name-line"><span className="agent-name-line__text">{text}</span><HybridMark agent={agent} /></span>
  : text);

/** Two-line name cell: real name on top, PHM nickname below. */
export const AgentNameDisplay = ({ agent, className = '' }) => {
  const real = agentRealName(agent);
  const nick = agentNickname(agent);
  if (real && nick && real.toLowerCase() !== nick.toLowerCase()) {
    return (
      <span className={`agent-name-stack${className ? ` ${className}` : ''}`}>
        <span className="agent-name-stack__real">{nameWithMark(real, agent)}</span>
        <span className="agent-name-stack__nick">{nick}</span>
      </span>
    );
  }
  return <span className={className || undefined}>{nameWithMark(real || nick || '-', agent)}</span>;
};
